import React, { useState } from "react";

import { ArrowDropDown } from "@mui/icons-material";
import { Box, Checkbox, FormControlLabel, Popover } from "@mui/material";

interface CardFilterSelectProps {
  label: string;
  options: readonly string[];
  selected: string[];
  onChange: (value: string[]) => void;
  tone: "blue" | "green";
}

export const CardFilterSelect: React.FC<CardFilterSelectProps> = ({ label, options, selected, onChange, tone }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const hasSelection = selected.length > 0;

  const toneStyles =
    tone === "blue"
      ? { activeBorder: "#3B82F6", activeColor: "#3B82F6" }
      : { activeBorder: "#496682", activeColor: "#496682" };

  const displayText = hasSelection ? selected.join(", ") : `Select ${label.toLowerCase()}`;

  return (
    <Box
      onClick={(e) => setAnchorEl(anchorEl ? null : e.currentTarget)}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        minHeight: 32,
        px: 3,
        py: 0.5,
        border: "1px solid",
        borderRadius: "8px",
        bgcolor: "#FFFFFF",
        cursor: "pointer",
        fontSize: 12,
        color: hasSelection ? "#071f35" : "#9CA3AF",
        "&:hover": { borderColor: hasSelection ? toneStyles.activeBorder : "#b0bbc7" }
      }}
    >
      <Box sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayText}</Box>
      {hasSelection && (
        <Box
          component="span"
          sx={{
            bgcolor: toneStyles.activeColor,
            color: "#FFFFFF",
            borderRadius: "999px",
            px: "5px",
            fontSize: 10,
            fontWeight: 800,
            lineHeight: "16px",
            mr: 0.5,
            flexShrink: 0
          }}
        >
          {selected.length}
        </Box>
      )}
      <Box component="span" sx={{ fontSize: 18, color: "#9CA3AF", display: "flex", ml: 0.5, flexShrink: 0 }}>
        <ArrowDropDown
          sx={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", color: "#5d7782" }}
        />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: "4px",
              borderRadius: "6px",
              border: "1px solid #dbe8f5",
              boxShadow: "0 4px 12px rgba(7,31,53,0.08)",
              minWidth: 200,
              maxWidth: 320,
              maxHeight: 200,
              overflowY: "auto"
            }
          }
        }}
      >
        {options.map((option) => {
          const checked = selected.includes(option);
          return (
            <FormControlLabel
              key={option}
              control={
                <Checkbox
                  checked={checked}
                  onChange={(e) => {
                    e.stopPropagation();
                    onChange(e.target.checked ? [...selected, option] : selected.filter((v) => v !== option));
                  }}
                  sx={{ p: "4px", "& .MuiSvgIcon-root": { fontSize: 18 } }}
                />
              }
              label={option}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                m: 0,
                p: "4px 8px",
                bgcolor: checked ? "#E8F5F0" : "transparent",
                "& .MuiFormControlLabel-label": {
                  fontSize: 13,
                  color: "#071f35",
                  fontWeight: checked ? 600 : 400,
                  maxWidth: 180,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }
              }}
            />
          );
        })}
        {hasSelection && (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              onChange([]);
              setAnchorEl(null);
            }}
            sx={{
              borderTop: "1px solid #dbe8f5",
              p: "6px 8px",
              fontSize: 12,
              color: "#d93e3e",
              fontWeight: 600,
              cursor: "pointer"
            }}
          >
            Clear {label.toLowerCase()}
          </Box>
        )}
      </Popover>
    </Box>
  );
};
